import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Stepper,
  Step,
  StepButton,
  Divider,
  Typography,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader/PageHeader';
import { CustomButton } from '../../components/Infra/CustomButton/CustomButton';
import { StepHeader } from '../../components/DataFeedWizard/StepHeader/StepHeader';
import { ShowFeedModal } from '../../components/DataFeedWizard/ShowFeedModal/ShowFeedModal';
import BaseModal from '../../components/Modal/BaseModal';
import { Configuration } from '../../components/DataFeed/Configuration/Configuration';
import { useDataFeedWizard } from './useDataFeedWizard';
import { useDataFeedWizardStore } from '../../store/dataFeedWizard';
import useConfigurationStore from '../../store/configurationStore';
import Actions from '../../store/Actions';
import { WIZARD_STEPS_CONFIG, WizardStep } from './wizardStepsConfig';
import { RowType } from '../../types/rowType';
import styles from './DataFeedWizardPage.module.scss';

export const DataFeedWizardPage: React.FC = () => {
  const [showFeedModalOpen, setShowFeedModalOpen] = useState(false);
  const [showFeedLoading, setShowFeedLoading] = useState(false);
  const [expandConfigModalOpen, setExpandConfigModalOpen] = useState(false);
  const [searchParams] = useSearchParams();

  const {
    sessionId: storedSessionId,
    sessionName,
    vaultFiles,
    excelFiles,
    restoredFiles,
    stepLoading,
    fileId,
    templateId,
    rowsType,
  } = useDataFeedWizardStore();
  const { setAvailableHeaders } = useConfigurationStore();

  const {
    activeStep,
    stepperSteps,
    currentMetadata,
    handleBack,
    handleBackToDataFeed,
    isFirstStep,
    nextButtonProps,
    validationMessage,
    initializeWizard,
    wizardInitializing,
  } = useDataFeedWizard();

  useEffect(() => {
    const urlSessionId = searchParams.get('session_id');
    initializeWizard(urlSessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showFeedEnabled =
    !!storedSessionId &&
    (vaultFiles.length > 0 ||
      excelFiles.length > 0 ||
      restoredFiles.length > 0);

  const handleShowFeed = async () => {
    if (!storedSessionId) return;

    setShowFeedLoading(true);
    try {
      // Load both raw and processed data before opening modal
      await Actions.getWizardFileData(storedSessionId);
      if (fileId) {
        await Actions.getProcessedFileData(fileId);
      }
      setShowFeedModalOpen(true);
    } catch (error) {
      console.error('Failed to load feed data:', error);
    } finally {
      setShowFeedLoading(false);
    }
  };
  const handleCloseShowFeed = () => setShowFeedModalOpen(false);

  const handleExpandConfiguration = async () => {
    if (!storedSessionId) return;

    try {
      await Actions.getWizardFileData(storedSessionId);
      const response = await Actions.getWizardFileColumns(storedSessionId);
      setAvailableHeaders(response.columns);
    } catch (error) {
      console.error('Failed to load file columns:', error);
    }

    setExpandConfigModalOpen(true);
  };
  const handleCloseExpandConfig = () => setExpandConfigModalOpen(false);

  const renderStepContent = () => {
    const stepConfig = WIZARD_STEPS_CONFIG[activeStep];
    const StepComponent = stepConfig.component;
    return <StepComponent />;
  };

  return (
    <Box className={styles.dataFeedWizard} data-testid="data-feed-wizard">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <PageHeader
          title={
            sessionName
              ? `Data Feed Upload - ${sessionName}`
              : 'Data Feed Upload'
          }
          subtitle="Upload and configure your data feed"
        />
        <CustomButton
          variant="text"
          onClick={handleBackToDataFeed}
          data-testid="btn-back-to-data-feed"
        >
          BACK TO DATA FEED
        </CustomButton>
      </Box>

      <Paper className={styles.wizardContainer} sx={{ position: 'relative' }}>
        {wizardInitializing && (
          <Box
            data-testid="wizard-loading"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              zIndex: 10,
              borderRadius: 'inherit',
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <Box className={styles.stepperContainer}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            nonLinear
            data-testid="wizard-stepper"
            sx={{
              '& .MuiStep-root': {
                '&.Mui-active': {
                  '& .MuiStepLabel-label': {
                    fontWeight: 600,
                    color: '#1a1a1a',
                  },
                },
                '&.Mui-completed': {
                  opacit: 0.8,
                  '& .MuiStepLabel-label': {
                    color: '#66666697',
                  },
                },
              },
            }}
          >
            {stepperSteps.map((step, index) => (
              <Step
                key={step.label}
                completed={step.completed && index !== activeStep}
                disabled={step.disabled}
              >
                <StepButton onClick={step.onClick} disabled={step.disabled}>
                  {step.label}
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </Box>
        <Divider />

        <Box className={styles.stepContent}>
          <StepHeader
            title={currentMetadata.title}
            subtitle={currentMetadata.subtitle}
            icon={currentMetadata.icon}
            onShowFeed={handleShowFeed}
            showFeedEnabled={showFeedEnabled}
            showFeedLoading={showFeedLoading}
            onExpandConfiguration={
              [
                WizardStep.LINK_INSTRUMENTS,
                WizardStep.LINK_OTC,
                WizardStep.REVIEW_AND_UPLOAD,
              ].includes(activeStep) && rowsType !== RowType.ROW_TYPE_POSITION
                ? handleExpandConfiguration
                : undefined
            }
          />
          <Box className={styles.content} sx={{ position: 'relative' }}>
            {renderStepContent()}
            {stepLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  zIndex: 1,
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </Box>
        </Box>

        <Box className={styles.actionButtons}>
          <Box className={styles.leftButtons}>
            <CustomButton
              variant="outlined"
              onClick={handleBack}
              disabled={isFirstStep}
              data-testid="wizard-btn-back"
            >
              Back
            </CustomButton>
          </Box>

          <Box className={styles.rightButtons}>
            {validationMessage && (
              <Typography
                variant="body2"
                color="error"
                className={styles.validationMessage}
                data-testid="wizard-validation-message"
              >
                {validationMessage}
              </Typography>
            )}
            <Tooltip
              title={
                nextButtonProps.disabled && validationMessage
                  ? validationMessage
                  : ''
              }
              placement="top"
            >
              <span>
                <CustomButton
                  variant="contained"
                  {...nextButtonProps}
                  data-testid="wizard-btn-next"
                />
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      <ShowFeedModal open={showFeedModalOpen} onClose={handleCloseShowFeed} />

      <BaseModal
        dataTestId="expand-config-modal"
        open={expandConfigModalOpen}
        onClose={handleCloseExpandConfig}
        title="Configuration"
        maxWidth="xl"
        hideSubmit
        hideCancel
        keepMounted
        sx={{ height: '100%' }}
      >
        <Configuration
          initialTemplateId={templateId}
          isVisible={expandConfigModalOpen}
        />
      </BaseModal>
    </Box>
  );
};
